import { cn } from '@qodo/design-system';
import { Folder, GitBranch, LoaderCircle } from 'lucide-react';
import { type GitlabProvisioningManageResult } from 'network/httpClients/GitlabClient';
import { getResultStatusClass } from './outcome';

export type ScopeResultListProps = {
  results: GitlabProvisioningManageResult[];
  /** Resolves the right-hand status label for a per-scope row (i18n lives in the container). */
  getResultStatusLabel: (result: GitlabProvisioningManageResult) => string;
  /** Resolves the left-hand scope label (defaults to the raw scope). */
  getResultLabel?: (result: GitlabProvisioningManageResult) => string;
  /** Show a group/repo icon before each label (used by the flat multi-group list). */
  showKindIcon?: boolean;
};

/**
 * The per-scope row list: each row shows its label and live status (in_progress spinner,
 * queued muted, terminal colored). Shared by the flat single-install view and the
 * sectioned multi-group view so both render rows identically.
 */
export const ScopeResultList = ({
  results,
  getResultStatusLabel,
  getResultLabel = (result) => result.scope,
  showKindIcon = false,
}: ScopeResultListProps) => (
  <ul className="flex flex-col gap-1.5">
    {results.map((result) => (
      <li
        key={`${result.kind}:${result.scope}`}
        className="flex items-center justify-between gap-4 text-sm"
      >
        <span className="flex items-center gap-2">
          {showKindIcon &&
            (result.kind === 'group' ? (
              <Folder className="size-4 shrink-0 text-icon-general-neutral-mid" />
            ) : (
              <GitBranch className="size-4 shrink-0 text-icon-general-neutral-mid" />
            ))}
          <span className="text-text-general-neutral-strong">
            {getResultLabel(result)}
          </span>
        </span>
        <span
          className={cn(
            'flex items-center gap-1.5 shrink-0',
            getResultStatusClass(result),
          )}
        >
          {result.status === 'in_progress' && (
            <LoaderCircle className="size-3.5 animate-spin" />
          )}
          {getResultStatusLabel(result)}
        </span>
      </li>
    ))}
  </ul>
);
